import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    type FilterOption,
    type FilterOptionItem,
    normalizeFilterOption,
    filterOptionMatches
} from '@odo/shared-ui';

/**
 * A labeled group of options within a single dropdown - e.g. "States" and
 * "Cities" under one "Location" pill. Each section owns its own selection and
 * onChange (they typically map to distinct query params server-side), but
 * share one search box and one combined active-count badge.
 */
export interface FilterDropdownSection {
    key: string;
    label: string;
    options: readonly FilterOption[];
    selectedOptions: string[];
    onChange: (selected: string[]) => void;
}

interface FilterDropdownBaseProps {
    title: string;
    align?: 'left' | 'center' | 'right';
    workQueueVariant?: boolean;
    /** Optional node rendered below the options list, separated by a top border. */
    footer?: React.ReactNode;
    /**
     * When true the pill reads as active even when no options are selected.
     * Instead of the count number, a small filled dot is shown in the badge slot.
     */
    forceActive?: boolean;
}

interface FilterDropdownFlatProps extends FilterDropdownBaseProps {
    options: readonly FilterOption[];
    selectedOptions: string[];
    onChange: (selected: string[]) => void;
    sections?: undefined;
}

interface FilterDropdownSectionedProps extends FilterDropdownBaseProps {
    /** Grouped options - each section renders under its own label header. */
    sections: readonly FilterDropdownSection[];
    options?: undefined;
    selectedOptions?: undefined;
    onChange?: undefined;
}

type FilterDropdownProps = FilterDropdownFlatProps | FilterDropdownSectionedProps;

// Internal, render-ready shape both prop modes normalize into. `label: null`
// means "flat mode" - no section header is rendered, matching the original
// single-list behavior exactly.
interface NormalizedSection {
    key: string;
    label: string | null;
    items: FilterOptionItem[];
    selectedOptions: string[];
    onChange: (selected: string[]) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = (props) => {
    const {
        title,
        align = 'center',
        workQueueVariant = false,
        footer,
        forceActive = false
    } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [isHoveringButton, setIsHoveringButton] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const normalizedSections = useMemo<NormalizedSection[]>(() => {
        if (props.sections) {
            return props.sections.map((section) => ({
                key: section.key,
                label: section.label,
                items: section.options
                    .map(normalizeFilterOption)
                    .filter((o) => filterOptionMatches(o, search)),
                selectedOptions: section.selectedOptions,
                onChange: section.onChange
            }));
        }
        return [
            {
                key: '__flat',
                label: null,
                items: props.options
                    .map(normalizeFilterOption)
                    .filter((o) => filterOptionMatches(o, search)),
                selectedOptions: props.selectedOptions,
                onChange: props.onChange
            }
        ];
    }, [props.sections, props.options, props.selectedOptions, props.onChange, search]);

    const totalSelectedCount = normalizedSections.reduce(
        (sum, section) => sum + section.selectedOptions.length,
        0
    );
    const hasAnyItems = normalizedSections.some((section) => section.items.length > 0);

    const clearAll = () => {
        normalizedSections.forEach((section) => section.onChange([]));
    };

    const toggleSectionOption = (section: NormalizedSection, value: string) => {
        const next = section.selectedOptions.includes(value)
            ? section.selectedOptions.filter((item) => item !== value)
            : [...section.selectedOptions, value];
        section.onChange(next);
    };

    const getAlignmentClasses = () => {
        switch (align) {
            case 'left':
                return 'left-0';
            case 'right':
                return 'right-0';
            case 'center':
            default:
                return 'left-1/2 -translate-x-1/2';
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            searchInputRef.current?.focus();
        }
    }, [isOpen]);

    const isActive = totalSelectedCount > 0 || forceActive;

    const showClearOnHover =
        workQueueVariant && isActive && isHoveringButton && !isOpen && totalSelectedCount > 0;

    const buttonClasses = [
        'inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2',
        'bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300',
        workQueueVariant ? 'whitespace-nowrap' : 'w-full',
        isOpen ? 'border-gray-700' : 'border-gray-300'
    ].join(' ');

    const menuContent = (
        <>
            <div className="sticky top-0 bg-white px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                        {title}
                    </span>
                    {isActive && (
                        <button
                            type="button"
                            className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                            onClick={clearAll}
                        >
                            clear
                        </button>
                    )}
                </div>
                <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                        placeholder={`Search ${title.toLowerCase()}...`}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                {!workQueueVariant && isActive && (
                    <button
                        type="button"
                        className="flex items-center gap-1 mt-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                        onClick={clearAll}
                    >
                        <XMarkIcon className="h-3.5 w-3.5" />
                        Clear {totalSelectedCount} selected
                    </button>
                )}
            </div>
            <div className="overflow-y-auto scrollbar-hide pb-1" style={{ maxHeight: '16rem' }}>
                {!hasAnyItems ? (
                    <div className="px-4 py-3 text-sm text-gray-400 text-center">No matches</div>
                ) : (
                    normalizedSections.map((section) => {
                        if (section.items.length === 0) return null;
                        return (
                            <div key={section.key}>
                                {section.label !== null && (
                                    <div className="px-4 pt-2 pb-1 text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                                        {section.label}
                                    </div>
                                )}
                                {section.items.map((option) => {
                                    const checked = section.selectedOptions.includes(option.value);
                                    return (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className="w-full flex items-center px-4 py-2 text-sm text-left hover:bg-gray-100"
                                            onClick={() =>
                                                toggleSectionOption(section, option.value)
                                            }
                                        >
                                            <input
                                                type="checkbox"
                                                className="h-4 w-4 text-gray-700 border-gray-300 rounded focus:ring-gray-500 pointer-events-none"
                                                checked={checked}
                                                readOnly
                                                style={{
                                                    accentColor: checked ? '#0A0A0A' : '#D4D4D4'
                                                }}
                                            />
                                            <span className="ml-3 min-w-0 flex-1 select-none">
                                                <span className="block truncate text-gray-700">
                                                    {option.label}
                                                </span>
                                                {option.sublabel && (
                                                    <span className="block truncate text-[11px] text-gray-400">
                                                        {option.sublabel}
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })
                )}
            </div>
            {footer != null && <div className="border-t border-gray-100">{footer}</div>}
        </>
    );

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            <button
                type="button"
                className={buttonClasses}
                style={workQueueVariant ? undefined : { minWidth: '160px' }}
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHoveringButton(true)}
                onMouseLeave={() => setIsHoveringButton(false)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span>{title}</span>
                <span className="flex items-center ml-2">
                    <span
                        className={`mr-1 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs leading-none transition-opacity duration-200 ${
                            isActive ? 'bg-gray-700 text-white opacity-100' : 'opacity-0'
                        }`}
                    >
                        {forceActive && totalSelectedCount === 0 ? (
                            <span className="block w-1.5 h-1.5 rounded-full bg-white" />
                        ) : (
                            totalSelectedCount
                        )}
                    </span>
                    {showClearOnHover ? (
                        // The × is its own click target: pill-click must keep opening the
                        // menu so an active multi-select can be refined, not just rebuilt.
                        <span
                            role="button"
                            aria-label={`Clear ${title} filter`}
                            className="flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                clearAll();
                            }}
                        >
                            <XMarkIcon className="h-4 w-4 m-0.5" />
                        </span>
                    ) : (
                        <ChevronDownIcon
                            className={`h-5 w-5 transition-transform duration-300 ${
                                isOpen ? 'transform rotate-180' : ''
                            }`}
                        />
                    )}
                </span>
            </button>

            <div
                className={`absolute ${getAlignmentClasses()} mt-2 w-64 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                    isOpen ? 'ring-gray-700' : 'ring-transparent'
                }`}
                style={{
                    maxHeight: isOpen ? '24rem' : '0',
                    opacity: isOpen ? 1 : 0
                }}
            >
                {menuContent}
            </div>
        </div>
    );
};

export default FilterDropdown;
